import { useState, useEffect } from "react";
import { Loader2, ArrowUpRight, ArrowDownRight, DollarSign, CreditCard, ChevronDown } from "lucide-react";
import {
  ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
  ComposedChart, Line,
  AreaChart, Area,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  ReferenceDot,
} from "recharts";
import api from "../../utils/api";
import MetricValue, { formatMetric } from "../../components/shared/MetricValue";
import "./IncomeExpensesTab.css";

const CARD = {
  borderRadius: 12,
  background: "#FFFFFF",
  boxShadow: "0 2px 8px rgba(26,32,44,0.06)",
  padding: 16,
};

/* Color tokens - applied consistently across charts, donuts, badges, trend arrows */
const COLOR = {
  navy: "#1A202C",
  muted: "#64748B",
  amber: "#F59E0B",          // MTD pill
  expense: "#F5841F",        // Operating Expenses (brand orange, shared across all tabs)
  blue: "#3B82F6",           // Total Revenue / Gross / positive trend / Net Income donut arc
  cyan: "#06B6D4",           // Net Income series + Operating Margin + Operating Income donut
  violet: "#8B5CF6",         // Net Profit Margin + Interest & Taxes donut
  gold: "#D4A017",           // Sales accent
  track: "#E2E8F0",
  tileBg: "#F7F9FC",
};

function fmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n); }
function fmtK(n) { if (n >= 1000000) return `$${(n/1000000).toFixed(1)}M`; if (n >= 1000) return `$${(n/1000).toFixed(0)}k`; return `$${n}`; }
function fmtTick(n) { if (n >= 1000000) return `${(n/1000000).toFixed(1)}M`; if (n >= 1000) return `${(n/1000).toFixed(0)}k`; return `${n}`; }

/* ---------- KPI Card (Row 1) ---------- */
function KpiCard({ icon: Icon, iconBg, title, value, prev, mtdPct, valueColor }) {
  return (
    <div style={CARD} data-testid={`kpi-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: iconBg }}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex flex-col items-end gap-1">
          <span style={{ background: COLOR.amber, color: "#FFFFFF", borderRadius: 4, fontSize: 11, fontWeight: 600, padding: "2px 8px" }}>MTD</span>
          <span className="flex items-center gap-0.5" style={{ color: COLOR.blue, fontSize: 11, fontWeight: 600 }}>
            <ArrowUpRight className="w-3 h-3" />{mtdPct}
          </span>
        </div>
      </div>
      <p style={{ fontSize: 10, color: COLOR.muted, marginTop: 8 }}>{title}</p>
      <p style={{ fontSize: 20, fontWeight: 700, color: valueColor || COLOR.navy, lineHeight: 1.1, marginTop: 4 }} data-testid={`kpi-value-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {typeof value === "number" ? (
          <MetricValue value={value} mode="kpi" charBudget={11} testid={`mv-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
        ) : value}
      </p>
      <p style={{ fontSize: 10, color: COLOR.muted, marginTop: 4 }}>PREV {prev}</p>
    </div>
  );
}

/* ---------- Donut with center label ---------- */
function GoalDonut({ label, valueText, goalText, color, size = "lg" }) {
  const inner = size === "lg" ? 45 : 35;
  const outer = size === "lg" ? 60 : 48;
  const centerFontSize = size === "lg" ? 16 : 14;
  const data = [{ name: "actual", value: 65 }, { name: "rest", value: 35 }];
  return (
    <div className="flex flex-col items-center" style={{ background: "#FFFFFF", borderRadius: 12, boxShadow: "0 2px 8px rgba(26,32,44,0.06)", padding: 12 }}>
      <p style={{ fontSize: 12, color: COLOR.muted, textAlign: "center", marginBottom: 4 }}>{label}</p>
      <div style={{ position: "relative", width: outer * 2 + 6, height: outer * 2 + 6 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" innerRadius={inner} outerRadius={outer} startAngle={90} endAngle={-270} stroke="none">
              <Cell fill={color} />
              <Cell fill={COLOR.track} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <span style={{ fontSize: centerFontSize, fontWeight: size === "lg" ? 700 : 600, color: COLOR.navy }}>{valueText}</span>
        </div>
      </div>
      <p style={{ fontSize: size === "lg" ? 11 : 10, color: COLOR.muted, marginTop: 6, textAlign: "center" }}>Financial Goal&nbsp;&nbsp;{goalText}</p>
    </div>
  );
}

/* ---------- Income Statement Tile ---------- */
function StatTile({ label, value, change, isWorkingCapital }) {
  const trendColor = isWorkingCapital ? COLOR.amber : COLOR.blue;
  const Arrow = isWorkingCapital ? ArrowDownRight : ArrowUpRight;
  const sign = isWorkingCapital ? "-" : "+";
  return (
    <div style={{ background: COLOR.tileBg, borderRadius: 8, padding: "10px 12px" }}>
      <p style={{ fontSize: 11, color: COLOR.muted }}>{label}</p>
      <p style={{ fontSize: 18, fontWeight: 700, color: COLOR.navy, lineHeight: 1.2, marginTop: 4 }}>{value}</p>
      <div className="flex items-center gap-1 mt-1">
        <Arrow className="w-3 h-3" style={{ color: trendColor }} />
        <span style={{ fontSize: 10, color: trendColor }}>{sign}{Math.abs(change ?? 6.2)}% from last month</span>
      </div>
    </div>
  );
}

/* ---------- Legend dot ---------- */
function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5" style={{ fontSize: 12, color: COLOR.navy }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />
      {label}
    </span>
  );
}

export default function IncomeExpensesTab({ summary, mtdSummary, dateRange }) {
  /* mtdSummary is locked to MTD and never re-fetches when global dateRange changes */
  const mtd = mtdSummary || summary;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateRange?.start) params.set("start_date", dateRange.start);
    if (dateRange?.end) params.set("end_date", dateRange.end);
    api.get(`/dashboard-data/income-expenses?${params.toString()}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateRange]);

  if (loading || !data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: COLOR.blue }} /></div>;

  const { income_statement: is, financial_goals: fg, revenue_expenses_breakdown: reb, profitability_margin: pm, revenue_trend_mom: rt } = data;

  const revTrendData = rt.months.map((m, i) => ({ month: m, "2023": rt.y2023[i], "2024": rt.y2024[i], "2025": rt.y2025[i] }));
  const breakdownData = reb.months.map((m, i) => ({ month: m, revenue: reb.revenue[i], expenses: reb.expenses[i], net: reb.net_income[i] }));
  const profitData = pm.months.map((m, i) => ({ month: m, gross: pm.gross_margin[i], operating: pm.operating_margin[i], net: pm.net_profit_margin[i] }));
  const refIndex = Math.floor(profitData.length / 2);
  const refPoint = profitData[refIndex];

  return (
    <div className="ie-page" data-testid="income-expenses-tab">
      {/* ---------- LEFT COLUMN ---------- */}
      <div className="ie-col-left">
        {/* Block 1: KPI row */}
        <div className="ie-kpi-row">
          <KpiCard icon={DollarSign} iconBg="#1B75BA" title="Balance by Bank Account (Cash)" value={mtd?.bank_balance ?? 32000} prev={fmt(mtd?.bank_prev || 38500)} mtdPct="56%" />
          <KpiCard icon={CreditCard} iconBg="#282560" title="Total Credit Card Balance" value={mtd?.credit_card_balance ?? 32000} prev={fmt(mtd?.credit_card_prev || 38500)} mtdPct="56%" valueColor={COLOR.blue} />
        </div>

        {/* Block 2: Financial Goals section label (not a card) */}
        <div className="ie-fg-label" data-testid="financial-goals-label">
          <h3 style={{ fontSize: 13, fontWeight: 700, color: COLOR.navy }}>Financial Goals</h3>
          <span style={{ background: COLOR.amber, color: "#FFFFFF", borderRadius: 4, fontSize: 11, fontWeight: 600, padding: "2px 8px" }}>MTD</span>
        </div>

        {/* Block 3: Donut row 1 (2 donuts) - Total Revenue blue, Operating Expenses orange (brand expense) */}
        <div className="ie-donut-row-2" data-testid="financial-goals">
          <GoalDonut label="Total Revenue" valueText={fmtK(fg.total_revenue.actual)} goalText={fmtK(fg.total_revenue.goal)} color={COLOR.blue} />
          <GoalDonut label="Operating Expenses" valueText={fmtK(fg.operating_expenses.actual)} goalText={fmtK(fg.operating_expenses.goal)} color={COLOR.expense} />
        </div>

        {/* Block 4: Donut row 2 (3 donuts) - purple/teal/gold (no red/green) */}
        <div className="ie-donut-row-3" data-testid="financial-goals-secondary">
          <GoalDonut label="Interest & Taxes" valueText={fmtK(fg.interest_taxes.actual)} goalText={fmtK(fg.interest_taxes.goal)} color={COLOR.violet} size="sm" />
          <GoalDonut label="Operating Income" valueText={fmtK(fg.operating_income.actual)} goalText={fmtK(fg.operating_income.goal)} color={COLOR.cyan} size="sm" />
          <GoalDonut label="Net Income" valueText={fmtK(fg.net_income.actual)} goalText={fmtK(fg.net_income.goal)} color={COLOR.gold} size="sm" />
        </div>

        {/* Block 5: Revenue Trend (MOM) */}
        <div style={CARD} data-testid="revenue-trend-mom">
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: COLOR.navy }}>Revenue Trend (MOM)</h3>
            <div className="flex items-center gap-3">
              <LegendDot color={COLOR.violet} label="2023" />
              <LegendDot color={COLOR.amber} label="2024" />
              <LegendDot color={COLOR.blue} label="2025" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revTrendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#F0F1F5" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLOR.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: COLOR.muted }} axisLine={false} tickLine={false} tickFormatter={fmtTick} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => formatMetric(v, { charBudget: 0, decimals: 2 })} />
              <Bar dataKey="2023" fill={COLOR.violet} radius={[3, 3, 0, 0]} />
              <Bar dataKey="2024" fill={COLOR.amber} radius={[3, 3, 0, 0]} />
              <Bar dataKey="2025" fill={COLOR.blue} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ---------- RIGHT COLUMN ---------- */}
      <div className="ie-col-right">
        {/* Block 1: Income Statement Analysis */}
        <div style={CARD} data-testid="income-statement-analysis">
          <h3 style={{ fontSize: 13, fontWeight: 700, color: COLOR.navy }}>Income Statement Analysis</h3>
          <div className="ie-isa-row">
            <StatTile label="Gross Margin" value={`${is.gross_margin.value}%`} change={is.gross_margin.change} />
            <StatTile label="Operating Margin" value={`${is.operating_margin.value}%`} change={is.operating_margin.change} />
            <StatTile label="Net Profit Margin" value={`${is.net_profit_margin.value}%`} change={is.net_profit_margin.change} />
            <StatTile label="Working Capital Days" value={`${is.working_capital_days.value} days`} change={is.working_capital_days.change} isWorkingCapital />
          </div>
        </div>

        {/* Block 2: Revenue & Expenses Breakdown */}
        <div style={CARD} data-testid="revenue-expenses-breakdown">
          <h3 style={{ fontSize: 13, fontWeight: 700, color: COLOR.navy, marginBottom: 12 }}>Revenue & Expenses Breakdown</h3>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={breakdownData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#F0F1F5" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLOR.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: COLOR.muted }} axisLine={false} tickLine={false} tickFormatter={fmtTick} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => formatMetric(v, { charBudget: 0, decimals: 2 })} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="revenue" name="Total Revenue" fill={COLOR.blue} radius={[3, 3, 0, 0]} />
              <Bar dataKey="expenses" name="Operating Expenses" fill={COLOR.expense} radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="net" name="Net Income" stroke={COLOR.cyan} strokeWidth={2} dot={{ r: 3, fill: COLOR.cyan }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Block 3: Profitability Margin */}
        <div style={CARD} data-testid="profitability-margin">
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: COLOR.navy }}>Profitability Margin</h3>
            <div className="flex items-center gap-3">
              <LegendDot color={COLOR.blue} label="Gross" />
              <LegendDot color={COLOR.cyan} label="Operating" />
              <LegendDot color={COLOR.violet} label="Net" />
              <button style={{ background: COLOR.navy, color: "#FFFFFF", borderRadius: 6, fontSize: 12, padding: "4px 12px", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="profitability-period-btn">
                This Week <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={profitData} margin={{ top: 8, right: 32, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="grossGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLOR.blue} stopOpacity={0.18} /><stop offset="100%" stopColor={COLOR.blue} stopOpacity={0} /></linearGradient>
                <linearGradient id="opGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLOR.cyan} stopOpacity={0.18} /><stop offset="100%" stopColor={COLOR.cyan} stopOpacity={0} /></linearGradient>
                <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COLOR.violet} stopOpacity={0.18} /><stop offset="100%" stopColor={COLOR.violet} stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="#F0F1F5" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLOR.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: COLOR.muted }} axisLine={false} tickLine={false} domain={[0, 12]} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
              <Area type="monotone" dataKey="gross" name="Gross Margin" stroke={COLOR.blue} fill="url(#grossGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="operating" name="Operating Margin" stroke={COLOR.cyan} fill="url(#opGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="net" name="Net Profit Margin" stroke={COLOR.violet} fill="url(#netGrad)" strokeWidth={2} />
              {refPoint && (
                <ReferenceDot
                  x={refPoint.month}
                  y={refPoint.gross}
                  r={4}
                  fill={COLOR.navy}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  label={{ value: `Gross Margin ${refPoint.gross}%`, position: "top", fill: COLOR.navy, fontSize: 11, offset: 8 }}
                  isFront
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
